"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import AvatarLookCard from "./avatarLookCard";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import type { AvatarLook, AvatarWithLooks } from "../types";

interface LookGalleryProps {
    avatar: AvatarWithLooks;
    selectedLookId?: string;
    onSelectLook: (look: AvatarLook) => void;
    onAddLook?: () => void;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function LookGallery({
    avatar,
    selectedLookId,
    onSelectLook,
    onAddLook,
    open,
    onOpenChange,
}: LookGalleryProps) {
    const { locale } = useLocale();
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>
                        {t("avatars.gallery.title", locale, {
                            name: avatar.name,
                        })}
                    </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {avatar.looks.map((look) => (
                        <AvatarLookCard
                            key={look.id}
                            look={look}
                            isSelected={look.heygenLookId === selectedLookId}
                            onSelect={(l) => {
                                onSelectLook(l);
                                onOpenChange(false);
                            }}
                        />
                    ))}
                    {onAddLook && (
                        <button
                            type="button"
                            onClick={onAddLook}
                            className="aspect-square rounded-lg border border-dashed flex flex-col items-center justify-center text-muted-foreground hover:bg-muted/50"
                        >
                            <Plus className="size-6 mb-1" />
                            <span className="text-xs">{t("avatars.gallery.add-look", locale)}</span>
                        </button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

/** Convenience hook so consumers don't have to manage open state inline. */
export function useLookGalleryState(initial = false) {
    const [open, setOpen] = useState(initial);
    return { open, setOpen, openGallery: () => setOpen(true) };
}
